import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowDown, ArrowUp, HelpCircle, Loader2, Plus, Save, Trash2,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { AdminError } from './components/PageState';

// Ana sayfadaki SSS akordeonunun içeriği (`settings.home_faq_items`).
//
// Metin koda gömülmez: sorular kuruluma göre değişir ve yazılım çok alıcıya
// satılıyor. Cevap portalda **düz metin** olarak çizilir (HTML çalıştırılmaz),
// bu yüzden burada da zengin metin alanı sunulmaz.
//
// Akordeon şu an yalnızca Herkül vitrininde çizilir; alan ayar kaydında
// durduğu için başka bir şema onu kullanmaya başlarsa içerik hazırdır.

const MAX_QUESTION = 300;
const MAX_ANSWER = 2000;

const HomeFaqEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/admin/settings');
      setItems(Array.isArray(data?.home_faq_items) ? data.home_faq_items : []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'SSS içeriği yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (index, field, value) => {
    setItems((prev) => prev.map((item, position) => (
      position === index ? { ...item, [field]: value } : item
    )));
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    setItems((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const save = async () => {
    // Boş soru satırı akordeonda tıklanabilir ama boş bir kutu bırakırdı.
    const cleaned = items
      .map((item) => ({
        question: String(item.question || '').trim().slice(0, MAX_QUESTION),
        answer: String(item.answer || '').trim().slice(0, MAX_ANSWER),
      }))
      .filter((item) => item.question);

    setSaving(true);
    try {
      await client.patch('/admin/settings', { home_faq_items: cleaned });
      setItems(cleaned);
      toast({ title: 'Kaydedildi', description: `${cleaned.length} soru yayında` });
    } catch (requestError) {
      toast({
        title: 'Kaydedilemedi',
        description: requestError.response?.data?.detail,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>;
  }
  if (error) return <AdminError message={error} onRetry={load} />;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div>
          <h2 className="font-bold">Sıkça Sorulan Sorular</h2>
          <p className="text-xs text-slate-400">
            Ana sayfanın altındaki akordeon · cevaplar düz metin olarak çizilir
          </p>
        </div>
        <button
          type="button"
          onClick={() => setItems((prev) => [...prev, { question: '', answer: '' }])}
          className="admin-btn-secondary ml-auto flex items-center gap-2"
        >
          <Plus size={14} /> Soru Ekle
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="admin-btn-primary flex items-center gap-2"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Kaydet
        </button>
      </div>

      {items.length === 0 ? (
        <div className="admin-card p-10 text-center text-slate-500">
          Soru yok. Liste boşken ana sayfada SSS bölümü hiç çizilmez.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((item, index) => (
            // Satırlar taşınıp silinebildiği için anahtar sıradan türetilir;
            // soru metni yazılırken değiştiği için anahtar olamaz.
            /* eslint-disable-next-line react/no-array-index-key */
            <div className="admin-card p-4" key={index}>
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(228,187,108,0.15)', color: 'var(--v2-warn, #e4bb6c)' }}
                >
                  <HelpCircle size={14} />
                </span>
                <span className="text-xs text-slate-400">{index + 1}. soru</span>
                <div className="flex gap-1 ml-auto">
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="icon-btn edit disabled:opacity-40"
                    title="Yukarı taşı"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1}
                    className="icon-btn edit disabled:opacity-40"
                    title="Aşağı taşı"
                  >
                    <ArrowDown size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setItems((prev) => prev.filter((_, p) => p !== index))}
                    className="icon-btn ban"
                    title="Soruyu sil"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              <input
                className="admin-input mb-2"
                placeholder="Soru"
                maxLength={MAX_QUESTION}
                value={item.question || ''}
                onChange={(event) => patch(index, 'question', event.target.value)}
              />
              <textarea
                className="admin-input"
                rows={3}
                placeholder="Cevap"
                maxLength={MAX_ANSWER}
                value={item.answer || ''}
                onChange={(event) => patch(index, 'answer', event.target.value)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HomeFaqEditor;
